import { useRef, useState, type ChangeEvent } from 'react';
import { Icon } from '../../../components/ui/Icon';
import { useEditorSelector, editorStore } from '../../editorStore';
import type { PanelCtx } from '../panelTypes';

interface ItemImageFieldProps {
  label: string;
  /** Uploaded asset id, or '' when the item still uses the design's own artwork. */
  assetId: string;
  ctx: PanelCtx;
  onChange: (assetId: string) => void;
}

/** A picture for one list item (a product, a book, a property...): upload a new one, pick an earlier upload, or go back to the artwork. */
export default function ItemImageField({ label, assetId, ctx, onChange }: ItemImageFieldProps) {
  const assets = useEditorSelector((state) => state.assets);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const thumbnail = assetId ? ctx.assetUrl(assetId) ?? assets[assetId]?.url ?? null : null;

  const choose = (id: string) => {
    setError(null);
    setOpen(false);
    onChange(id);
  };

  const handleFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    const uploaded = await editorStore.uploadAsset(file);
    if (uploaded) choose(uploaded.assetId);
    else setError('Could not upload this image. Please try a smaller file.');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="dwi-field dwi-image-field">
      <span className="dw-label">{label}</span>
      <div className="dw-image-slot-header">
        <div className="dw-thumbnail">
          {thumbnail ? (
            <img src={thumbnail} alt={label} />
          ) : (
            <span className="dw-thumbnail-placeholder">
              <Icon name="image" size={20} />
            </span>
          )}
        </div>
        <div className="dw-image-slot-info">
          <p>{assetId ? 'Custom image' : 'Original design artwork'}</p>
        </div>
        <div className="dw-image-slot-actions">
          <button type="button" className="dw-button dw-button--ghost" onClick={() => setOpen((v) => !v)}>
            <Icon name={assetId ? 'edit' : 'plus'} size={16} />
            <span>{assetId ? 'Change' : 'Add picture'}</span>
          </button>
          {assetId ? (
            <button type="button" className="dw-button dw-button--ghost dw-button--danger" onClick={() => choose('')}>
              <Icon name="trash" size={16} />
              <span>Remove</span>
            </button>
          ) : null}
        </div>
      </div>

      {error ? (
        <div role="alert" className="dw-alert dw-alert--error">
          {error}
        </div>
      ) : null}

      {open ? (
        <div className="dw-picker">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            className="dw-hidden-file"
            onChange={handleFile}
            aria-label={`Upload ${label.toLowerCase()}`}
          />
          <div className="dw-picker-actions">
            <button type="button" className="dw-button dw-button--primary" onClick={() => fileInputRef.current?.click()}>
              <Icon name="upload" size={16} />
              <span>Upload image</span>
            </button>
          </div>
          {Object.keys(assets).length > 0 ? (
            <div className="dw-asset-grid">
              {Object.values(assets).map((asset) => (
                <button
                  key={asset.assetId}
                  type="button"
                  className="dw-asset-tile"
                  onClick={() => choose(asset.assetId)}
                  aria-label={`Use ${asset.assetId}`}
                >
                  <img src={asset.url} alt={asset.alt || asset.assetId} />
                </button>
              ))}
            </div>
          ) : (
            <p className="dw-muted">No uploaded images yet. Upload one above.</p>
          )}
        </div>
      ) : null}
    </div>
  );
}
